import { useState } from 'react'
import { routeValidationErrors } from '../../api/validationError'
import { emit } from '../../platform/analytics/analytics'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { useToast } from '../../platform/feedback/ToastProvider'
import { CategoryIcon } from '../../platform/icons/CategoryIcon'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { IconPaneDialog, IconPaneOpener, useIconPane } from '../../platform/icons/IconPane'
import { CategoriesError, useCreateCategory, useUpdateCategory } from './useCategories'
import { CATEGORY_TYPE_LABEL, type Category } from './categoryTree'
import './categories.css'

const TYPES = ['expense', 'income', 'transfer'] as const

/** `CategoriesError`'s own status, narrowed from `unknown` in one place so
 *  neither caller below has to repeat the `instanceof` check. */
function statusOf(cause: unknown): number | undefined {
  return cause instanceof CategoriesError ? cause.status : undefined
}

/**
 * The two server failures worth putting on the field rather than in a toast.
 *
 * **404 on a save means "not yours", not "gone."** Both write handlers select
 * on `user_id` before checking `is_system`, so a system category falls out at
 * the not-found branch and never reaches the 403. Telling the user the
 * category was deleted elsewhere — or refetching as though it had been —
 * would be wrong on the only path that actually produces this.
 */
function messageFor(status: number | undefined, editing: boolean): string {
  if (status === 404 && editing) return 'This category cannot be edited.'
  if (status === 400) return 'A category with this name already exists.'
  return editing ? 'The category could not be saved.' : 'The category could not be created.'
}

/** The one field on this form with its own error slot (`Field`'s `error`
 *  prop) — a 422's `loc` may also name `icon` or `category_type`, neither of
 *  which has an error-display control of its own, so those are surfaced at
 *  form level instead, through the existing toast idiom. Same shape
 *  `MerchantForm.tsx`'s own `KNOWN_FIELDS` takes. */
const KNOWN_FIELDS = ['name'] as const

/**
 * The single source for every group heading's visible text — read by the
 * JSX below AND by `describeOtherEntry`'s toast copy, so renaming a heading
 * cannot leave the toast quoting the old word (architecture review round 2,
 * issue #473 non-blocking 4). A wire identifier (`icon`, `category_type`) is
 * never shown bare either way: a raw `loc` segment in front of a user is the
 * same class of defect this ticket fixes, one layer up. Same shape
 * `MerchantForm.tsx`'s own `FIELD_LABELS` takes.
 */
const FIELD_LABELS = {
  icon: 'Icon',
  category_type: 'Type',
} as const

/** One 422 entry the form has no error slot for, as one line of toast copy —
 *  labelled when `FIELD_LABELS` has an entry for its field, bare otherwise. */
function describeOtherEntry(entry: { field: string | undefined; message: string }): string {
  const label =
    entry.field !== undefined && entry.field in FIELD_LABELS
      ? FIELD_LABELS[entry.field as keyof typeof FIELD_LABELS]
      : undefined
  return label !== undefined ? `${label}: ${entry.message}` : entry.message
}

/**
 * A modal, on `Overlay` — which already owns the portal, the scrim, the focus
 * trap, Escape, focus restore and the inert background, so none of that is
 * re-implemented here. `Dialog.tsx` is not the host: it exports
 * `AlertDialog`/`ConfirmDialog`, both `role="alertdialog"` with a fixed body
 * and one or two fixed controls, and a form is neither.
 *
 * **The panel is glass** (`surface="glass"`), which is the design's look and
 * is safe HERE specifically: `overlay.css` keeps panels opaque by default
 * because a translucent one let `--neg-soft` content over the lime hero
 * measure 1.47:1, and this dialog carries no soft fill at all — it is fields
 * and buttons. A dialog with a destructive control still gets the opaque
 * surface.
 *
 * **The icon picker is a PANE of this dialog, never a second one — #455.**
 * `platform/icons/IconPane` owns all of it since #461 (the merchant form
 * picks from the same catalogue and got the same pane): the two layouts, the
 * layered Escape, the focus into the search field and back to "Choose icon",
 * and the phone's unmount-not-hide. Read that file for why each is what it
 * is. What stays here is the state the FORM owns — the pick, which only
 * `submit` may send.
 *
 * `editing` switches the form between create and edit. Four things change
 * with it: the initial field values, which mutation `submit` calls, the
 * submit control's label, and whether placement is offered or merely stated.
 */
export function CategoryForm({
  onClose,
  editing,
  parents = [],
  initialNested = false,
}: {
  onClose: () => void
  editing?: Category
  /** Top-level categories a NEW category may be placed under. Empty by
   *  default so a caller that only edits need not supply it — editing cannot
   *  re-parent, so the list would be unused there. */
  parents?: Category[]
  /** Open with "Subcategory of…" already chosen, so the parent chips render.
   *  Only the probe fixture sets it — a sweep cannot click. */
  initialNested?: boolean
}) {
  const [name, setName] = useState(editing?.name ?? '')
  const [type, setType] = useState<(typeof TYPES)[number]>(editing?.category_type ?? 'expense')
  // `parent_id` is on `CategoryCreate` and NOT on `CategoryUpdate` — verified
  // against the generated schema — so a category can be placed when it is
  // created and can never be re-parented afterwards. "Place it" is therefore
  // absent when editing rather than present and inert.
  const [parentId, setParentId] = useState<string | null>(null)
  // Whether the user has asked for a subcategory at all, kept SEPARATE from
  // which parent: "Subcategory of…" is chosen before any parent is, and a
  // single nullable id cannot express that intermediate state — it would snap
  // back to Top level the moment the segment was pressed.
  const [nested, setNested] = useState(initialNested)
  const [error, setError] = useState<string | undefined>(undefined)
  // The pane, and with it `picked` — the icon chosen IN THIS SESSION, never
  // the stored one. That distinction is the whole wire rule: `icon` is sent
  // only when `pane.picked` is set. A stored value may be a legacy slug the
  // catalogue does not know, and resending it unchanged would 422 a save that
  // did not touch the icon (#445/#446). `storedIcon` is what the grid shows as
  // SELECTED, which sends nothing.
  const pane = useIconPane({ storedIcon: editing?.icon })
  const picked = pane.picked
  const create = useCreateCategory()
  const update = useUpdateCategory()
  const toast = useToast()
  // `'form'` (33px), matching the `Field` and `Button size="form"` controls
  // this fieldset sits between; below 640px every size class alike returns the
  // coarse 44px floor.
  const typeRowHeight = useHitTarget('form')

  const pending = create.isPending || update.isPending

  async function submit() {
    const trimmed = name.trim()
    if (trimmed === '') {
      setError('Give the category a name.')
      return
    }
    // **No client-side duplicate check, and its removal is a fix rather than a
    // simplification.** There used to be one, on the premise that a name
    // identifies a category. The backend 270 taxonomy ends that: `name` is a display
    // label now and `key`/`id` are the identifiers, so names repeat by design
    // — fourteen categories are called "Others" and "Electronics" appears
    // three times. A check that refuses any name already in the list would
    // block a user from creating perfectly legal ones.
    //
    // The server was always the authority; it still answers a genuine
    // conflict with a 400, and that lands on this field via `messageFor`
    // below exactly as the local check's message did.

    // Asked for a subcategory and picked no parent. Refused here rather than
    // quietly created at top level, which is a different category from the one
    // the user asked for.
    if (nested && parentId === null) {
      setError('Choose which category this sits under.')
      return
    }

    try {
      // `icon` only when the user picked one this session, and then exactly
      // the slug the catalogue returned — backend #401 accepts only the
      // catalogue's 1,518 slugs and 422s anything else. Otherwise the key is
      // OMITTED, not sent as null: on an edit that leaves the stored value
      // untouched, which matters because it may be a legacy value that would
      // itself 422 if resent.
      const iconField = picked === null ? {} : { icon: picked.slug }
      if (editing) {
        const updated = await update.mutateAsync({
          id: editing.id,
          // No `color` key — #454 removed colour from the web app entirely.
          // Omitted rather than sent as `null`, so an edit that never touched
          // colour leaves whatever the backend already has (iOS still writes
          // one) untouched rather than clearing it.
          body: { name: trimmed, category_type: type, ...iconField },
        })
        toast.show({ kind: 'success', message: `${updated.name} saved` })
        // `custom_color_used` keeps the event's shape (rule 7) but is always
        // `false` — there is no colour control left to have used.
        emit('category_edited', {
          custom_color_used: false,
          custom_icon_used: picked !== null,
        })
      } else {
        const created = await create.mutateAsync({
          name: trimmed,
          category_type: type,
          ...iconField,
          // Omitted rather than sent as null when top-level: `parent_id` is
          // optional on the wire, and an explicit null is a different claim
          // from an absent field to a server that may distinguish them.
          ...(parentId === null ? {} : { parent_id: parentId }),
        })
        toast.show({ kind: 'success', message: `${created.name} created` })
        emit('category_created', {
          custom_color_used: false,
          custom_icon_used: picked !== null,
        })
      }
      onClose()
    } catch (cause) {
      // A 400 collided with something the client-side check above could not
      // see, and a 404 on an edit means "not yours" — both go ON THE FIELD,
      // matching how a 400 the check DID catch is presented.
      const status = statusOf(cause)
      if (status === 400 || (status === 404 && editing)) {
        setError(messageFor(status, Boolean(editing)))
        return
      }
      // A 422 is FastAPI's own genuine validation failure — issue #473. The
      // real body is `{ detail: ValidationError[] }`, never a bare string,
      // and EVERY entry is surfaced: one routed onto the Name field when its
      // `loc` names it, the rest folded into one toast, labelled where this
      // form has a label for the field (`FIELD_LABELS`) and bare otherwise —
      // never the wire's own field name. Falls through to `messageFor`'s
      // generic copy only when the body carries no usable `detail` at all.
      //
      // `.cause`, not `.body` — `CategoriesError` carries the raw wire body
      // through the standard `Error.cause` option (its own constructor,
      // `useCategories.ts`), unlike `MerchantsError`/`HouseholdError`, which
      // each declare their own `body` field.
      if (status === 422) {
        const body = cause instanceof CategoriesError ? cause.cause : undefined
        const { fieldMessages, otherEntries } = routeValidationErrors(body, KNOWN_FIELDS)
        const nameMessages = fieldMessages.get('name')
        if (nameMessages !== undefined || otherEntries.length > 0) {
          if (nameMessages !== undefined) setError(nameMessages.join('; '))
          if (otherEntries.length > 0) {
            toast.show({ kind: 'error', message: otherEntries.map(describeOtherEntry).join('; ') })
          }
          return
        }
      }
      toast.show({ kind: 'error', message: messageFor(status, Boolean(editing)) })
    }
  }

  const trimmed = name.trim()
  // The footer's own hint, and the reason the submit is disabled. Stated as
  // text rather than left to a disabled button the user has to guess at.
  const blockedBecause =
    trimmed === '' ? 'Give it a name to continue' : pending ? 'Saving…' : null

  return (
    <IconPaneDialog
      pane={pane}
      onClose={onClose}
      surface="glass"
      label={editing ? `Edit ${editing.name}` : 'New category'}
    >
      <form
        className="category-form"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <div className="category-form__head">
          {/* A live preview of the row: the icon —
              the one picked here if there is one, otherwise whatever the
              category already carries. `CategoryIcon` draws `icon_url` when
              there is one and otherwise falls through `icon` → the typed
              name → `Uncategorized`, so a NEW category with no pick still
              resolves sensibly the moment its name matches a system
              category's, and an EDITED one keeps showing its stored icon
              regardless of what the name field says.

              No colour: #454 removed it, so this circle is the same neutral
              `--chip` fill for every category — `categories.css` sets it
              directly, with nothing passed from here. */}
          <span className="category-form__preview" aria-hidden="true">
            <CategoryIcon
              category={{
                name: trimmed,
                icon: picked?.slug ?? editing?.icon,
                // `picked`'s URL, or the stored one — never a mix of a picked
                // slug with the stored URL.
                icon_url: picked === null ? editing?.icon_url : picked.icon_url,
              }}
            />
          </span>
          <span className="category-form__headings">
            <h3 className="category-form__title">{editing ? 'Edit category' : 'New category'}</h3>
            <p className="category-form__subtitle">
              Available in budgets, reports and auto-rules as soon as you save.
            </p>
          </span>
          <button
            type="button"
            className="category-form__close hoverable"
            style={{ minHeight: typeRowHeight, minWidth: typeRowHeight }}
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <Field
          label="Name"
          value={name}
          error={error}
          size="form"
          placeholder="e.g. Home office"
          onChange={(e) => {
            setName(e.target.value)
            setError(undefined)
          }}
        />

        {/* The icon — #447, opened as a pane since #455. A disclosure rather
            than an always-open grid: the catalogue is 1,518 icons across 53
            groups, and fetching it on every form would cost icon requests for
            a user who only came to rename. The picker is on the backend's
            catalogue and nothing else, because the category endpoints 422 any
            slug the catalogue did not return. */}
        <div className="category-form__group">
          <span className="category-form__group-label" aria-hidden="true">
            {FIELD_LABELS.icon}
          </span>
          <IconPaneOpener pane={pane} />
        </div>

        <div className="category-form__group">
          <span className="category-form__group-label" aria-hidden="true">
            {FIELD_LABELS.category_type}
          </span>
          {/* A glass segmented control, not a stack of radios. The radios are
              still REAL — one per segment, native, keyboard-navigable as a
              group — they are just stretched invisibly over their own pill
              (`inset: 0; opacity: 0`), which is the mechanism `IconGrid`
              already uses. NOT `.sr-only`: that collapses the input
              to 1px, and `e2e/hitTargets.mjs` measures the INPUT, so a
              screen-reader-only radio would fail the 44px floor — the exact
              defect the last sweep caught on the search field. */}
          <div className="category-form__seg" role="radiogroup" aria-label="Type">
            {TYPES.map((t) => (
              <label key={t} className="category-form__seg-item" style={{ minHeight: typeRowHeight }}>
                <input
                  className="category-form__input"
                  type="radio"
                  name="category_type"
                  checked={type === t}
                  onChange={() => setType(t)}
                />
                {CATEGORY_TYPE_LABEL[t]}
              </label>
            ))}
          </div>
        </div>

        {/* Editing states the placement instead of offering it.
            `CategoryUpdate` carries no `parent_id` — verified against the
            generated schema — so the API cannot re-parent a category at all,
            and M11-R17 is explicit that a control with no endpoint behind it
            is ABSENT, not disabled.

            But absent-and-silent is what made this read as a bug: the segment
            simply vanished between creating and editing, with nothing saying
            why. A control is not the same thing as the information, and the
            information is useful on its own — this is the only place, other
            than the detail screen, that tells you where a category sits. */}
        {editing !== undefined && (
          <div className="category-form__group">
            <span className="category-form__group-label" aria-hidden="true">
              Place it
            </span>
            <p className="category-form__note">
              {editing.parent_id == null
                ? 'At the top level.'
                : `Sits under ${parents.find((p) => p.id === editing.parent_id)?.name ?? 'another category'}.`}{' '}
              Where a category sits is fixed when it is created.
            </p>
          </div>
        )}

        {/* Create only — see the `parentId` declaration. Two steps, matching
            the design's own `placements` pills and `parents` chips: the
            segment chooses WHETHER this is a subcategory, and the chips choose
            of what. A select handled 88 parents in one control but hid the
            choice behind a popup; chips put it on the surface, which is what
            the design does and what makes the common case (top level) a single
            glance. */}
        {editing === undefined && (
          <div className="category-form__group">
            <span className="category-form__group-label" aria-hidden="true">
              Place it
            </span>
            <div className="category-form__seg" role="radiogroup" aria-label="Placement">
              <label className="category-form__seg-item" style={{ minHeight: typeRowHeight }}>
                <input
                  className="category-form__input"
                  type="radio"
                  name="placement"
                  checked={!nested}
                  onChange={() => {
                    setNested(false)
                    // Cleared, not remembered: leaving a parent selected while
                    // the segment says "Top level" is two pieces of state
                    // disagreeing, and the one that would get submitted is the
                    // invisible one.
                    setParentId(null)
                  }}
                />
                Top level
              </label>
              <label className="category-form__seg-item" style={{ minHeight: typeRowHeight }}>
                <input
                  className="category-form__input"
                  type="radio"
                  name="placement"
                  checked={nested}
                  onChange={() => setNested(true)}
                />
                Subcategory of…
              </label>
            </div>

            {nested && (
              <div className="category-form__chips" role="radiogroup" aria-label="Parent category">
                {parents.map((p) => (
                  <label
                    key={p.id}
                    className="category-form__chip"
                    style={{ minHeight: typeRowHeight, minWidth: typeRowHeight }}
                  >
                    <input
                      className="category-form__input"
                      type="radio"
                      name="parent"
                      checked={parentId === p.id}
                      onChange={() => setParentId(p.id)}
                    />
                    {p.name}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Drawn because the design draws it, INERT because there is no
            auto-rules module to create a rule in (#287/#288) — and switched
            OFF, not on. The design defaults it on; defaulting a switch on
            when nothing behind it runs would promise every user a rule that
            is never created, which is the one version of this that actually
            misleads. A span rather than a disabled input, for the reason the
            table's sort segments are spans: a disabled skin in a route sheet
            makes it a control sheet. */}
        <div className="category-form__rule">
          <span className="category-form__switch" aria-hidden="true">
            <span className="category-form__knob" />
          </span>
          <span>
            <span className="category-form__rule-title">Create a matching auto-rule</span>
            <span className="category-form__rule-body">
              Future transactions whose merchant contains the name would land here
              automatically — once auto-rules ship. Nothing is created yet.
            </span>
          </span>
        </div>

        <div className="category-form__actions">
          {blockedBecause !== null && (
            <span className="category-form__hint">{blockedBecause}</span>
          )}
          {/* `chip` and `primary`, matching the design's own pair: the
              dismissive action takes the quiet fill and the committing one
              takes the accent. `outline` and the default `chip` were what
              shipped first, which left the primary action looking like the
              secondary one. */}
          <Button type="button" variant="chip" size="form" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="form"
            disabled={pending || trimmed === ''}
          >
            {editing ? (pending ? 'Saving…' : 'Save') : pending ? 'Creating…' : 'Create category'}
          </Button>
        </div>
      </form>
    </IconPaneDialog>
  )
}
